// Load an environment splat with THREE's own loaders — no Spark.
//
// three r186 ships a loader for every format `environment_assets.format` allows:
// `SPZLoader` (spz v1-4), `GaussianSplatPLYLoader`, `SPLATLoader`, `KSPLATLoader`.
// The long-standing claim that it had no spz loader is what kept Spark on this
// path; measured on the real 43.5 MB Startup_Level.spz, `SPZLoader` parses it in
// 1.8 s to the same 2,406,393 splats Spark reports.
//
// AND IT KEEPS THE SPHERICAL HARMONICS, which the Spark route throws away:
// `forEachSplat` yielded only the DC colour, so the hand-off filled three's
// `color` from SH0 and leaves `sphericalHarmonics1..3` empty — the environment
// has had no view-dependent colour under three, a fidelity loss CLAUDE.md
// records as open. The loader emits those bands already packed in three's own
// layout, so they pass straight through.
//
// THE POSE IS BAKED IN, for the same reason the Spark adoption bakes it: the
// avatar and the environment share ONE GaussianSplat so they depth-sort against
// each other, and a mesh carries a single matrix, so everything in it must
// already be in the same space.
//
// THIS MODULE FETCHES, RATHER THAN LETTING THE LOADER DO IT, and that is the one
// structural choice here. three's loaders would fetch the URL themselves through
// `FileLoader` — which means the BROWSER's HTTP cache, and the AAM serves these
// with `max-age=86400` under a URL that does not change when the file does. A
// re-uploaded environment would then serve its old bytes for a day, silently,
// and the app's own "Clear Cache" could not help because that clears Cache
// Storage, a different store.
//
// So the bytes come from `splatCache` — the app's Cache-Storage bucket, keyed by
// (url, the row's `updatedAt`) — or off the wire with `cache: 'no-store'`, and
// `loader.parse` takes it from there. One path, one cache, and the version is
// what invalidates it. It also means a warm reload of a 43-130 MB room is
// instant, which the loader's own fetch never gave us.

import * as THREE from 'three'
import { estimateGeometryFloorY } from '../../../lib/splatFloorHeight.js'
import * as splatCache from '../../../lib/splatCache.js'

const LOADERS = {
  spz: () => import('three/examples/jsm/loaders/SPZLoader.js').then((m) => new m.SPZLoader()),
  ply: () => import('three/examples/jsm/loaders/GaussianSplatPLYLoader.js').then((m) => new m.GaussianSplatPLYLoader()),
  splat: () => import('three/examples/jsm/loaders/SPLATLoader.js').then((m) => new m.SPLATLoader()),
  ksplat: () => import('three/examples/jsm/loaders/KSPLATLoader.js').then((m) => new m.KSPLATLoader()),
}

/**
 * Fetch the bytes, streaming so the caller sees real byte progress.
 *
 * `cache: 'no-store'` on purpose. `splatCache` is THE cache for these files,
 * keyed by the row's `updatedAt`; letting the browser keep a second copy would
 * duplicate 43-130 MB per environment AND — because the AAM serves them with
 * `max-age=86400` — serve a re-uploaded environment's OLD bytes for a day, under
 * an unchanged URL, with nothing on screen to say so.
 */
async function fetchWithProgress(url, onProgress, init = { cache: 'no-store' }) {
  const res = await fetch(url, init)
  if (!res.ok) throw new Error(`HTTP ${res.status} fetching ${url}`)
  const total = Number(res.headers.get('content-length')) || 0
  // No body reader (or no length) — fall back to the whole thing at once and
  // report UNKNOWN rather than a bar that sits at zero and then jumps.
  if (!res.body?.getReader) {
    onProgress?.({ loaded: 0, total, lengthComputable: total > 0 })
    const buf = await res.arrayBuffer()
    onProgress?.({ loaded: buf.byteLength, total: total || buf.byteLength, lengthComputable: true })
    return buf
  }
  const reader = res.body.getReader()
  const chunks = []
  let loaded = 0
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    loaded += value.byteLength
    onProgress?.({ loaded, total, lengthComputable: total > 0 })
  }
  const out = new Uint8Array(loaded)
  let at = 0
  for (const c of chunks) { out.set(c, at); at += c.byteLength }
  return out.buffer
}

/** Format from the row when it has one, else the URL's extension. Never guessed
 *  from content: a wrong loader reads garbage as splats rather than failing. */
export function splatFormat(url, declared) {
  const fmt = String(declared ?? '').toLowerCase()
  if (LOADERS[fmt]) return fmt
  const ext = String(url ?? '').split('?')[0].split('.').pop().toLowerCase()
  return LOADERS[ext] ? ext : null
}

/**
 * @param {object} o
 * @param {string} o.url
 * @param {string} [o.format]
 * @param {THREE.Matrix4} [o.matrix]  world placement to bake in
 * @returns {Promise<{count:number, centre:Float32Array, covA:Float32Array,
 *   covB:Float32Array, colour:Uint32Array, sh:object|null}>}
 */
/**
 * CACHE FIRST, PARSE, AND ON A PARSE FAILURE EVICT AND TRY ONCE MORE.
 *
 * Extracted from `loadEnvironmentGeometry` and given its dependencies as
 * arguments so a unit test can drive THIS function rather than a paraphrase of
 * it. That distinction is not pedantry here: the two failures this sequence
 * exists to prevent are both silent (a re-upload serving stale bytes; a
 * truncated body replaying forever), so a test that re-stated the sequence would
 * agree with itself and prove nothing about what ships.
 *
 * @param {object} o
 * @param {string} o.url
 * @param {string|number|null} o.version  the splat-cache key (the row's `updatedAt`)
 * @param {{matchArrayBuffer: Function, store: Function, evict: Function}} o.cache
 * @param {(init?: object) => Promise<ArrayBuffer>} o.fetchBytes
 * @param {(buf: ArrayBuffer) => any} o.parse
 * @param {(e: object) => void} [o.onProgress]
 * @param {(msg: string) => void} [o.warn]
 * @param {(msg: string) => void} [o.info]
 * @returns {Promise<{parsed: any, fromCache: boolean}>}
 */
export async function acquireSplat({
  url, version, cache, fetchBytes, parse, onProgress, warn = console.warn, info = console.info,
}) {
  // A MISS ON A BUMPED VERSION IS THE POINT. A re-upload lands under the same
  // URL, so the version is the only thing that can notice it; with no version
  // there is nothing that could ever invalidate an entry, so nothing is stored.
  let buf = version ? await cache.matchArrayBuffer(url, version) : null
  const fromCache = buf != null
  if (fromCache) {
    onProgress?.({ loaded: buf.byteLength, total: buf.byteLength, lengthComputable: true })
  } else {
    buf = await fetchBytes()
    // Best effort, deliberately not awaited: a quota failure must cost the NEXT
    // load a re-download, never this load its environment.
    if (version) Promise.resolve(cache.store(url, version, buf)).catch(() => {})
  }

  try {
    return { parsed: await parse(buf), fromCache }
  } catch (first) {
    // RETRY ONCE, PAST EVERY CACHE — because a cache is where the bad copy is. A
    // truncated body otherwise survives every reload: the environment loads once
    // and then fails on every load after, which reads as a server problem.
    //
    // ONE retry only. If the SERVER's copy is bad, asking again cannot fix it and
    // the caller has to hear about it.
    const why = String(first?.message ?? first)
    warn(`[env-load] failed to decode (${why}); re-fetching past every cache`)
    // Drops EVERY version of this url: a body that will not parse is a property
    // of the file, not of the version we happened to ask for.
    await cache.evict(url)
    const fresh = await fetchBytes({ cache: 'reload' })
    const parsed = await parse(fresh)
    if (version) Promise.resolve(cache.store(url, version, fresh)).catch(() => {})
    info('[env-load] the re-fetched copy decoded — the cached one was bad')
    return { parsed, fromCache: false }
  }
}

/**
 * The loaded geometry with the pose BAKED INTO ITS OWN ATTRIBUTES — position and
 * covariance rewritten in place, colour and spherical harmonics untouched.
 *
 * This is what `new GaussianSplat(geo)` wants, so it is the shape the app uses
 * when three draws the environment directly into the R3F scene. `loadEnvironmentSplats`
 * below then repacks it into the bridge's own buffer layout, which is a different
 * job — the bridge writes GPU buffers behind an already-built mesh.
 *
 * Baking rather than setting `mesh.matrix` is not a shortcut: the environment
 * shares ONE mesh with the avatar on the bridge path, a mesh carries a single
 * matrix, and keeping both paths in world space means they cannot drift.
 *
 * @param {object} o
 * @param {string} o.url  the row's `file_url` or a part's `url`, verbatim
 * @param {string} [o.format]  the row's declared format; the extension otherwise
 * @param {string|number|null} [o.version]  the row's `updatedAt`. It is the
 *   splat-cache key — omit it and every load re-downloads; get it wrong and a
 *   re-upload serves stale bytes. Both are silent, which is why it is explicit.
 * @param {THREE.Matrix4|null} [o.matrix]  world placement to bake in
 * @param {(e: {loaded: number, total: number, lengthComputable: boolean}) => void} [o.onProgress]
 * @returns {Promise<THREE.BufferGeometry>}
 */
export async function loadEnvironmentGeometry({
  url, format, version = null, matrix = null, onProgress = undefined,
}) {
  const fmt = splatFormat(url, format)
  if (!fmt) throw new Error(`no three loader for "${url}" (format ${format ?? 'unknown'})`)
  const loader = await LOADERS[fmt]()

  const { parsed: geo } = await acquireSplat({
    url,
    version,
    // `store` takes a Blob and `acquireSplat` deals in ArrayBuffers, so the wrap
    // is HERE rather than in the sequence: what the real cache wants is the real
    // cache's business, and keeping the sequence buffer-shaped is what lets a
    // test hand it a plain fake.
    cache: {
      matchArrayBuffer: splatCache.matchArrayBuffer,
      evict: splatCache.evict,
      store: (u, v, buf) => splatCache.store(u, v, new Blob([buf])),
    },
    // The bytes, streamed so the caller sees real byte progress, and with
    // `cache: 'no-store'` so the browser keeps no second 43-130 MB copy under a
    // URL that does not change when the file does.
    fetchBytes: (init) => fetchWithProgress(url, onProgress, init ?? { cache: 'no-store' }),
    parse: (buf) => loader.parse(buf),
    onProgress,
  }).catch((e) => {
    // NAME THE LIKELY CAUSE. A cut-short gzip stream surfaces as `RangeError:
    // Array buffer allocation failed` from inside the inflate, because the
    // corrupt length is what gets allocated — indistinguishable from "out of
    // memory" unless it is said here.
    throw new Error(
      `the ${fmt} at "${url}" could not be decoded, before or after bypassing the `
      + `caches (${String(e?.message ?? e)}). A truncated or corrupt body reads `
      + 'like this, so the served file is likely bad.',
      { cause: e },
    )
  })

  const pos = geo.getAttribute('position')
  const cov = geo.getAttribute('covariance')
  const col = geo.getAttribute('color')
  if (!pos || !cov || !col) throw new Error(`${fmt} loader returned no splat attributes`)
  if (!matrix) return geo

  const m = matrix.elements
  // Linear part of the placement. A covariance transforms as A·C·Aᵀ — NOT by the
  // matrix directly, and not by the scale alone: a rotated environment whose
  // covariances were left untouched renders every splat with the wrong long axis.
  const a = [m[0], m[4], m[8], m[1], m[5], m[9], m[2], m[6], m[10]]
  const p = pos.array
  const c = cov.array
  for (let i = 0; i < pos.count; i++) {
    const x = p[i * 3], y = p[i * 3 + 1], z = p[i * 3 + 2]
    p[i * 3]     = m[0] * x + m[4] * y + m[8] * z + m[12]
    p[i * 3 + 1] = m[1] * x + m[5] * y + m[9] * z + m[13]
    p[i * 3 + 2] = m[2] * x + m[6] * y + m[10] * z + m[14]
    const o = i * 6
    const [c00, c01, c02, c11, c12, c22] = transformCovariance(
      a, c[o], c[o + 1], c[o + 2], c[o + 3], c[o + 4], c[o + 5])
    c[o] = c00; c[o + 1] = c01; c[o + 2] = c02
    c[o + 3] = c11; c[o + 4] = c12; c[o + 5] = c22
  }
  pos.needsUpdate = true
  cov.needsUpdate = true
  return geo
}

/** C' = A·C·Aᵀ for a symmetric C given as its upper triangle, A row-major.
 *  Exported for the unit test: it is the one piece of real maths on this path,
 *  and getting it wrong renders every splat with the wrong long axis — which
 *  looks like a bad scan rather than a bad transform. */
export function transformCovariance(a, c00, c01, c02, c11, c12, c22) {
  const m00 = a[0] * c00 + a[1] * c01 + a[2] * c02
  const m01 = a[0] * c01 + a[1] * c11 + a[2] * c12
  const m02 = a[0] * c02 + a[1] * c12 + a[2] * c22
  const m10 = a[3] * c00 + a[4] * c01 + a[5] * c02
  const m11 = a[3] * c01 + a[4] * c11 + a[5] * c12
  const m12 = a[3] * c02 + a[4] * c12 + a[5] * c22
  const m20 = a[6] * c00 + a[7] * c01 + a[8] * c02
  const m21 = a[6] * c01 + a[7] * c11 + a[8] * c12
  const m22 = a[6] * c02 + a[7] * c12 + a[8] * c22
  return [
    m00 * a[0] + m01 * a[1] + m02 * a[2],
    m00 * a[3] + m01 * a[4] + m02 * a[5],
    m00 * a[6] + m01 * a[7] + m02 * a[8],
    m10 * a[3] + m11 * a[4] + m12 * a[5],
    m10 * a[6] + m11 * a[7] + m12 * a[8],
    m20 * a[6] + m21 * a[7] + m22 * a[8],
  ]
}

/**
 * @param {object} o
 * @param {string} o.url
 * @param {string} [o.format]
 * @param {string|number|null} [o.version]  the row's `updatedAt`, the splat-cache
 *   key. Threaded through for the same reason `loadEnvironmentGeometry` takes it:
 *   dropping it here is silent and costs a 43-130 MB re-download on every reload
 *   of the AVATAR-owned path, which is the path a splat character always takes.
 * @param {THREE.Matrix4|null} [o.matrix]
 * @param {(e: object) => void} [o.onProgress]
 */
export async function loadEnvironmentSplats({
  url, format, version = null, matrix = null, onProgress = undefined,
}) {
  const geo = await loadEnvironmentGeometry({ url, format, version, matrix, onProgress })
  const pos = geo.getAttribute('position')
  const cov = geo.getAttribute('covariance')
  const col = geo.getAttribute('color')
  const n = pos.count

  const centre = new Float32Array(n * 4)
  const covA = new Float32Array(n * 4)
  const covB = new Float32Array(n * 4)
  const colour = new Uint32Array(n)

  // The geometry arrives POSED, so this is a pure repack into the layout three's
  // GaussianSplat keeps internally: centre and covariance as vec4s, colour packed
  // RGBA8 into a u32 — the same layout `uploadStaticTail` writes.
  const p = pos.array, c = cov.array, cl = col.array
  for (let i = 0; i < n; i++) {
    centre[i * 4] = p[i * 3]
    centre[i * 4 + 1] = p[i * 3 + 1]
    centre[i * 4 + 2] = p[i * 3 + 2]
    centre[i * 4 + 3] = 1

    // Upper triangle, three's order: c00 c01 c02 c11 c12 c22.
    covA[i * 4] = c[i * 6]
    covA[i * 4 + 1] = c[i * 6 + 1]
    covA[i * 4 + 2] = c[i * 6 + 2]
    covA[i * 4 + 3] = c[i * 6 + 3]
    covB[i * 4] = c[i * 6 + 4]
    covB[i * 4 + 1] = c[i * 6 + 5]

    const o = i * 4
    colour[i] = ((cl[o] | (cl[o + 1] << 8) | (cl[o + 2] << 16) | (cl[o + 3] << 24)) >>> 0)
  }

  // SH straight through: the loader already packs them in three's layout, so
  // there is no conversion and — unlike the Spark route — nothing is dropped.
  let sh = null
  const bands = [1, 2, 3].filter((b) => geo.getAttribute(`sphericalHarmonics${b}`))
  if (bands.length) {
    sh = { degree: bands.length }
    for (const b of bands) sh[`sh${b}`] = geo.getAttribute(`sphericalHarmonics${b}`).array
  }
  // THE FLOOR TRAVELS WITH THE SPLATS, because both consumers need it and only one
  // of them was computing it. `__ENV_FLOOR_Y__` places the avatar, the ground plane
  // and the shadow sun; `ThreeEnvironmentSplat` estimates it for a GLB character's
  // room, and for a SPLAT character `Scene.jsx` does not mount that component at
  // all (the avatar owns the environment), so nothing did — the character stood at
  // y=0 whatever the room said. Returning it here means the estimate cannot go
  // missing on a path that loads the environment.
  //
  // The positions are already WORLD space (the pose is baked in above), which is
  // the frame the estimator documents.
  const floorY = estimateGeometryFloorY(geo)
  geo.dispose?.()
  return { count: n, centre, covA, covB, colour, sh, floorY }
}
